import { useState } from 'react';
import { Banner, Button, Card, Dialog, Field, FileDrop, formatMoney, Money, Pill } from '@elysian/ui';
import type { OpenItemRow, OpenItemsCheck } from '../../data/source';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { iso, shortDate } from '../invoices/shared';
import { KINDS, PACKAGES, readOpenItems, type ImportKind, type ImportPackage } from './importFile';

const ACTION = { add: { tone: 'ok', label: 'Add' }, skip: { tone: 'draft', label: 'Skip' }, problem: { tone: 'warn', label: 'Problem' } } as const;
const readText = (f: File) => (typeof f.text === 'function' ? f.text()
  : new Promise<string>((ok, no) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = () => no(r.error); r.readAsText(f); }));

const count = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/** The last day of the month before today's: the usual "as at" for a dry run (and 31 December in January). */
function lastMonthEnd(today: Date) {
  return iso(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0)));
}

/**
 * Unpaid invoices and credit notes (or bills and supplier credits) at the migration date, from Sage's
 * aged debtors (creditors) export (go-live item 9; db/040): read in the browser, every row shown with what
 * it will do and who it's for, then staged as import batches to import below with the usual backup.
 */
export function ImportOpenItems({ pkg, kind, onPkg, onKind, onClose, onStaged }: {
  pkg: ImportPackage; kind: 'invoices' | 'purchases';
  onPkg: (p: ImportPackage) => void; onKind: (k: ImportKind) => void;
  onClose: () => void; onStaged: (msg: string) => void;
}) {
  const { source, today } = useSession();
  const toast = useToast((t) => t.show);
  const sales = kind === 'invoices';
  const [asAt, setAsAt] = useState(() => lastMonthEnd(today()));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = useState<{ file: string; rows: OpenItemRow[]; result: OpenItemsCheck } | null>(null);
  const p = PACKAGES.find((x) => x.key === pkg)!;
  const who = sales ? 'customer' : 'supplier';

  async function read(file: File) {
    setBusy(true);
    setError(null);
    try {
      const r = readOpenItems(await readText(file));
      if (r.problem) { setError(r.problem); return; }
      const result = await source.checkOpenItems({ kind: sales ? 'sales' : 'purchases', as_at: asAt, source: pkg === 'sage50' ? 'sage50' : 'sage_accounting',
        file_name: file.name, apply: false, rows: r.rows });
      setChecked({ file: file.name, rows: r.rows, result });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function stage() {
    if (!checked) return;
    setBusy(true);
    setError(null);
    try {
      const r = await source.checkOpenItems({ kind: sales ? 'sales' : 'purchases', as_at: asAt, source: pkg === 'sage50' ? 'sage50' : 'sage_accounting',
        file_name: checked.file, apply: true, rows: checked.rows });
      const msg = `${count(r.invoices, sales ? 'invoice' : 'bill')} and ${count(r.credits, 'credit note')} ready to import below`;
      toast(msg);
      onStaged(msg);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  if (!checked) {
    return (
      <Dialog title="Import data" onClose={onClose} actions={<Button variant="ghost" onClick={onClose}>Cancel</Button>}>
        <div className="el-form-grid">
          <Field id="imp-pkg" label="From">
            <select id="imp-pkg" className="el-input" value={pkg} onChange={(e) => onPkg(e.target.value as ImportPackage)}>
              {PACKAGES.map((x) => <option key={x.key} value={x.key} disabled={!x.ready}>{x.label}{x.ready ? '' : ' (coming soon)'}</option>)}
            </select>
          </Field>
          <Field id="imp-kind" label="What to import">
            <select id="imp-kind" className="el-input" value={kind} onChange={(e) => onKind(e.target.value as ImportKind)}>
              {KINDS.map((x) => <option key={x.key} value={x.key} disabled={!x.ready}>{x.label}</option>)}
            </select>
          </Field>
          <Field id="imp-asat" label="Unpaid as at" hint="The day before you start in the Ledger: 31 December for 1 January">
            <input id="imp-asat" type="date" className="el-input" value={asAt} onChange={(e) => setAsAt(e.target.value)} />
          </Field>
        </div>
        <p className="el-hint" style={{ margin: '0 0 10px' }}>
          {p.key === 'sage_accounting'
            ? `In Sage Accounting: Reporting → Aged ${sales ? 'Debtors' : 'Creditors'} → Detailed, as at that date → Export (CSV).`
            : p.how}{' '}
          Each {sales ? 'invoice and credit note' : 'bill and supplier credit'} keeps its Sage number and date; the total brings the opening
          balances control (9998) back to zero. A CSV with the columns Type, Number, Account, Name, Date, Due date, Total, Outstanding works too.
        </p>
        <FileDrop label={`Choose the aged ${sales ? 'debtors' : 'creditors'} file`} accept=".csv,text/csv" disabled={busy || !asAt}
          hint={busy ? 'Reading…' : 'Nothing is imported yet: you’ll see every row first.'}
          onFiles={(files) => files[0] && void read(files[0])} />
        {error && <div className="el-error-box" role="alert">{error}</div>}
      </Dialog>
    );
  }

  const r = checked.result;
  const problems = r.rows.filter((x) => x.action === 'problem').length;
  const adding = r.invoices + r.credits;
  const net = (Number(r.invoice_total) - Number(r.credit_total)).toFixed(2);

  return (
    <Card as="section" padding="18px 0 8px" aria-labelledby="oi-review">
      <div className="el-card-head el-txn-head">
        <div>
          <h2 id="oi-review" className="el-h2">Check the unpaid {sales ? 'invoices' : 'bills'} from {checked.file}</h2>
          <div className="el-hint">
            As at {shortDate(asAt)}: {count(r.invoices, sales ? 'invoice' : 'bill')} (<Money value={r.invoice_total} />) and {count(r.credits, 'credit note')} (<Money value={r.credit_total} />)
            to add, <Money value={net} /> net. Nothing is imported until you confirm, then import them below.
          </div>
        </div>
        <div className="el-row" style={{ gap: 8 }}>
          <Button variant="ghost" disabled={busy} onClick={() => { setChecked(null); setError(null); }}>Choose another file</Button>
          <Button variant="primary" disabled={busy || problems > 0 || adding === 0} onClick={() => void stage()}>
            {busy ? 'Getting ready…' : `Get ${adding} ready to import`}
          </Button>
        </div>
      </div>
      <div style={{ padding: '0 16px' }}>
        {r.difference_after !== null && r.difference_after !== undefined && (
          Number(r.difference_after) === 0
            ? <Banner tone="info">These match the {sales ? 'debtors' : 'creditors'} in the opening trial balance: 9998 comes back to zero.</Banner>
            : <Banner>After these, {formatMoney(Math.abs(Number(r.difference_after)).toFixed(2))} would still be {Number(r.difference_after) > 0 ? 'missing from' : 'more than'} the {sales ? 'debtors' : 'creditors'} in the opening trial balance (on 9998). Check the file covers every {who} at {shortDate(asAt)}.</Banner>
        )}
        {(r.target === null || r.target === undefined) && <Banner>Import the opening trial balance too, so these can be checked against its {sales ? 'debtors' : 'creditors'}.</Banner>}
        {problems > 0 && <div className="el-error-box" role="alert">{problems} {problems === 1 ? 'row has a problem' : 'rows have problems'}: put {problems === 1 ? 'it' : 'them'} right in the file, or delete {problems === 1 ? 'that row' : 'those rows'}, then choose it again.</div>}
        {error && <div className="el-error-box" role="alert">{error}</div>}
      </div>
      <div className="el-table-wrap">
        <table className="el-table" aria-label="Unpaid items to import">
          <thead>
            <tr><th scope="col">Row</th><th scope="col">Number</th><th scope="col">{sales ? 'Customer' : 'Supplier'}</th><th scope="col" className="el-num">Unpaid</th><th scope="col">Will be</th><th scope="col">Notes</th></tr>
          </thead>
          <tbody>
            {r.rows.map((x) => (
              <tr key={x.row} className={x.action === 'skip' ? 'el-row-off' : undefined}>
                <td className="el-muted">{x.row}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{x.number ?? <span className="el-muted">(none)</span>}{x.type === 'credit' && <div className="el-hint">Credit note</div>}</td>
                <td>{x.contact_name ?? <span className="el-muted">(none)</span>}</td>
                <td className="el-num">{x.outstanding ? <Money value={x.type === 'credit' ? `-${x.outstanding}` : x.outstanding} /> : '—'}</td>
                <td><Pill tone={ACTION[x.action].tone}>{ACTION[x.action].label}</Pill></td>
                <td className="el-hint">{x.notes.join('. ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
